import React, { ReactNode } from 'react'
import classNames from 'classnames'
import { Button } from '@hub/button'
import { Box } from '@hub/box'
import { Link } from '@hub/link'
import { Icon, TimedDisplay } from '@scentregroup/ui'
import useMobileBreakpoint from '@scentregroup/shared/helpers/use-mobile-breakpoint'

import Spinner from '../spinner'
import { useEvolvedContext } from '../../context'
import style from './index.module.scss'
import { RenderedSectionResults } from './types'
import { logSeeAllProducts } from './analytics'

const SPINNER_DELAY = 250
const MAX_PRODUCT_ITEMS = 5
const MAX_PRODUCT_ITEMS_MOBILE = 4

interface ProductResultsProps {
  hits: ReactNode[]
  showFixedQuery?: string
}

const ProductResults: React.FC<ProductResultsProps> = ({
  hits,
  showFixedQuery,
}) => {
  const isMobile = useMobileBreakpoint()
  return (
    <React.Fragment>
      <nav className={classNames(style.resultsLinks, style.products)}>
        {hits.slice(0, isMobile ? MAX_PRODUCT_ITEMS_MOBILE : MAX_PRODUCT_ITEMS)}
      </nav>
      <Box sx={{ textAlign: 'center', marginTop: 'spacing-lg' }}>
        <Button
          as={props => (
            <Link href={`/shop/products/?q=${showFixedQuery}`} {...props} />
          )}
          colorScheme="licorice"
          size="lg"
          sx={{ width: ['100%', 'size-20'], textDecoration: 'none !important' }}
          onClick={logSeeAllProducts}
        >
          SEE ALL PRODUCTS
        </Button>
      </Box>
    </React.Fragment>
  )
}
interface WestfieldProductResultsProps {
  hits: ReactNode[]
}

const WestfieldProductResults: React.FC<WestfieldProductResultsProps> = ({
  hits,
}) => {
  const isMobile = useMobileBreakpoint()
  return (
    <nav className={classNames(style.resultsLinks, style.products)}>
      {hits.slice(0, isMobile ? MAX_PRODUCT_ITEMS_MOBILE : MAX_PRODUCT_ITEMS)}
    </nav>
  )
}
interface SearchResultsProps {
  isLoading: boolean
  showNoResults: boolean
  results: RenderedSectionResults[]
  showFixedQuery?: string
}

const Contents: React.FC<SearchResultsProps> = ({
  isLoading,
  showNoResults,
  results,
  showFixedQuery,
}) => {
  const isEvolved = useEvolvedContext()
  if (isLoading && isEvolved) {
    return (
      <TimedDisplay timing={SPINNER_DELAY}>
        {null}
        <Spinner className={style.Spinner} />
      </TimedDisplay>
    )
  } else if (showNoResults && isEvolved) {
    return (
      <div className={style.noResults}>
        <Icon className={style.alertIcon} type="alert" size="medium" />
        <p>No results found.</p>
        <p>Try a new search to find more results.</p>
      </div>
    )
  } else {
    const sections = results.map(({ heading, hits }, index) => {
      let section = <nav className={style.resultsLinks}>{hits}</nav>
      if (heading === 'Products') {
        section = <ProductResults hits={hits} showFixedQuery={showFixedQuery} />
      } else if (heading === 'Westfield Gift Cards') {
        section = <WestfieldProductResults hits={hits} />
      }
      return (
        <section className={style.category} key={index}>
          <h2 className={style.heading}>{heading}</h2>
          {section}
        </section>
      )
    })
    return <>{sections}</>
  }
}

const SearchResults: React.FC<SearchResultsProps> = props => (
  <div className={style.results}>
    {props.showFixedQuery !== undefined && (
      <h1 className={style.mainHeading}>
        Results for "{props.showFixedQuery}"
      </h1>
    )}
    <Contents {...props} />
  </div>
)

export default SearchResults
